import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { FileText, Loader2, AlertCircle, CheckCircle2, ArrowLeft, ExternalLink, Info, ChevronDown, Eye } from "lucide-react";
import api from "../api/api";
import OrgChecklistReportCard from "./OrgChecklistReportCard";

export default function OrgChecklistTab({ resumes, isDarkMode, formatDate, formatFileSize, onPreviewResume }) {
    const [orgCode, setOrgCode] = useState("");
    const [orgCodeValidating, setOrgCodeValidating] = useState(false);
    const [orgCodeValid, setOrgCodeValid] = useState(null);
    const [orgCodeName, setOrgCodeName] = useState("");
    const [selectedResumeId, setSelectedResumeId] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checking, setChecking] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    // Sequence guard so a slow validation response can't overwrite a newer edit.
    const validateSeq = useRef(0);

    const validateOrgCode = useCallback(async (raw) => {
        const code = (raw || "").trim();
        const seq = ++validateSeq.current;
        if (!code) { setOrgCodeValid(null); setOrgCodeName(""); setOrgCodeValidating(false); return; }
        setOrgCodeValidating(true);
        try {
            const res = await api.get(`/resume/validate-org-code?code=${encodeURIComponent(code)}`);
            if (seq !== validateSeq.current) return;
            if (res.data.valid) {
                setOrgCodeValid(true);
                setOrgCodeName(res.data.orgName || "");
            } else {
                setOrgCodeValid(false);
                setOrgCodeName("");
            }
        } catch {
            if (seq !== validateSeq.current) return;
            setOrgCodeValid(false);
            setOrgCodeName("");
        } finally {
            if (seq === validateSeq.current) setOrgCodeValidating(false);
        }
    }, []);

    const fetchUsage = useCallback(async () => {
        try {
            const res = await api.get("/resume/org-check-usage");
            setUsage(res.data);
        } catch {
            setUsage(null);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [checksRes] = await Promise.all([api.get("/resume/my-org-checks"), fetchUsage()]);
                if (cancelled) return;
                const lastCode = checksRes.data.orgChecks?.[0]?.OrgCode;
                if (lastCode) {
                    setOrgCode(lastCode);
                    validateOrgCode(lastCode);
                }
            } catch {
                // no history yet
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [fetchUsage, validateOrgCode]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleOrgCodeChange = (e) => {
        validateSeq.current++;
        setOrgCode(e.target.value);
        setOrgCodeValid(null);
        setOrgCodeName("");
        setOrgCodeValidating(false);
    };

    const pollCheck = async (orgCheckId) => {
        const maxAttempts = 30;
        for (let i = 0; i < maxAttempts; i++) {
            await new Promise((r) => setTimeout(r, 2000));
            try {
                const res = await api.get(`/resume/org-check/${orgCheckId}`);
                if (res.data.Status === "completed") {
                    setResult(res.data);
                    setChecking(false);
                    fetchUsage();
                    return;
                }
                if (res.data.Status === "failed") {
                    setError(res.data.ErrorMessage || "Checklist evaluation failed. Please try again.");
                    setChecking(false);
                    fetchUsage();
                    return;
                }
            } catch {
                // keep polling
            }
        }
        setError("The checklist is taking longer than expected. Check Review History → Resume Checklists in a moment.");
        setChecking(false);
        fetchUsage();
    };

    const handleRun = async (e) => {
        e.preventDefault();
        setError("");

        if (orgCodeValid !== true) {
            setError("Enter a valid organization code first");
            return;
        }
        if (!selectedResumeId) {
            setError("Select a resume first");
            return;
        }

        setChecking(true);
        setResult(null);
        try {
            const res = await api.post("/resume/org-check", {
                resumeId: selectedResumeId,
                orgCode: orgCode.trim(),
            });
            pollCheck(res.data.orgCheckId);
        } catch (err) {
            setError(err.response?.data?.error || "Could not start the checklist");
            setChecking(false);
        }
    };

    const selectedResume = resumes.find((r) => String(r.ResumeID) === String(selectedResumeId));

    const cardClass = `rounded-2xl border shadow-sm p-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`;
    const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`;
    const inputClass = `w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
        isDarkMode
            ? "bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400"
            : "border-slate-300 text-black placeholder-slate-400"
    }`;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-primary-500" />
            </div>
        );
    }

    if (result) {
        return (
            <section>
                <button
                    onClick={() => setResult(null)}
                    className={`flex items-center gap-2 text-sm font-medium mb-4 transition-colors ${
                        isDarkMode ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                    <ArrowLeft size={16} />
                    Back to Resume Checklist
                </button>
                <OrgChecklistReportCard check={result} isDarkMode={isDarkMode} />
            </section>
        );
    }

    return (
        <>
            <div className={`flex items-start gap-3 px-4 py-3 rounded-xl mb-6 text-sm ${
                isDarkMode ? "bg-slate-800/60 text-slate-300 border border-slate-700" : "bg-primary-50 text-slate-600 border border-primary-100"
            }`}>
                <Info size={16} className="shrink-0 mt-0.5 text-primary-500" />
                <span>Check your resume against your university or organization's standards. Enter the code they gave you, choose a resume, and hit <strong>Analyze Resume</strong>.</span>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4 flex items-center gap-2">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            {usage && (
                <div className={`flex items-center justify-end gap-2 text-xs mb-4 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    <span><span className="font-medium">{usage.remaining}</span> of {usage.monthlyLimit} checklist runs remaining this month</span>
                    <Link
                        to="/membership/plans"
                        className={`font-semibold whitespace-nowrap px-3 py-1 rounded-full transition-colors ${isDarkMode ? "text-primary-200 bg-primary-600 hover:bg-primary-500" : "text-primary-700 bg-primary-100 hover:bg-primary-200"}`}
                    >
                        Upgrade Now
                    </Link>
                </div>
            )}

            <section className="mb-8">
                <div className={cardClass}>
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <CheckCircle2 size={20} />
                        Resume Checklist
                    </h2>

                    <form onSubmit={handleRun}>
                        <div className="mb-4">
                            <label className={labelClass}>Organization Code</label>
                            <input
                                type="text"
                                placeholder="Enter your organization code"
                                value={orgCode}
                                onChange={handleOrgCodeChange}
                                onBlur={() => validateOrgCode(orgCode)}
                                maxLength={32}
                                autoComplete="off"
                                className={`${inputClass} ${orgCodeValid === false ? "border-red-500" : orgCodeValid === true ? "border-green-500" : ""}`}
                            />
                            {orgCodeValidating ? (
                                <p className={`text-xs mt-1 flex items-center gap-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                    <Loader2 size={12} className="animate-spin" /> Validating...
                                </p>
                            ) : orgCodeValid === true && orgCodeName ? (
                                <p className="text-xs mt-1 text-green-500 flex items-center gap-1">
                                    <CheckCircle2 size={12} /> {orgCodeName}
                                </p>
                            ) : orgCodeValid === false ? (
                                <p className="text-xs mt-1 text-red-500">Unrecognized organization code</p>
                            ) : (
                                <p className={`text-xs mt-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                                    Provided by your university or organization.
                                </p>
                            )}
                        </div>

                        <div className="mb-4">
                            <label className={labelClass}>Resume</label>
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                    className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg text-sm transition-colors ${
                                        isDarkMode
                                            ? "bg-slate-700 border-slate-600 text-slate-100 hover:border-primary-500"
                                            : "bg-white border-slate-300 text-slate-900 hover:border-primary-500"
                                    }`}
                                >
                                    <span className={selectedResume ? "" : isDarkMode ? "text-slate-400" : "text-slate-500"}>
                                        {selectedResume ? (selectedResume.NickName || selectedResume.FileName) : "Select a resume"}
                                    </span>
                                    <ChevronDown size={16} className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                                </button>

                                {dropdownOpen && (
                                    <div className={`absolute z-10 mt-1 w-full rounded-lg border shadow-lg ${
                                        isDarkMode ? "bg-slate-700 border-slate-600" : "bg-white border-slate-200"
                                    }`}>
                                        {resumes.map((r) => (
                                            <div
                                                key={r.ResumeID}
                                                onClick={() => {
                                                    setSelectedResumeId(r.ResumeID);
                                                    setDropdownOpen(false);
                                                }}
                                                className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                                                    String(selectedResumeId) === String(r.ResumeID)
                                                        ? isDarkMode ? "bg-primary-900/30" : "bg-primary-50"
                                                        : isDarkMode ? "hover:bg-slate-600" : "hover:bg-slate-50"
                                                }`}
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <FileText size={16} className="shrink-0 text-primary-500" />
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium truncate">{r.NickName || r.FileName}</p>
                                                        <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                                            {formatFileSize(r.FileSizeBytes)} &middot; {formatDate(r.CreatedOn)}
                                                        </p>
                                                    </div>
                                                </div>
                                                {onPreviewResume && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onPreviewResume(r.ResumeID);
                                                        }}
                                                        title="Preview"
                                                        className="shrink-0 p-1.5 rounded-lg transition-colors text-slate-400 hover:text-primary-500"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}

                                        <Link
                                            to="/preferences" state={{ scrollTo: "resumes" }}
                                            onClick={() => setDropdownOpen(false)}
                                            className={`flex items-center gap-3 px-4 py-3 transition-colors border-t ${
                                                isDarkMode
                                                    ? "border-slate-600 hover:bg-slate-600 text-primary-400"
                                                    : "border-slate-100 hover:bg-primary-50 text-primary-600"
                                            }`}
                                        >
                                            <ExternalLink size={16} />
                                            <span className="text-sm font-medium">Manage Resumes</span>
                                        </Link>
                                    </div>
                                )}
                            </div>

                            {resumes.length === 0 && (
                                <Link
                                    to="/preferences" state={{ scrollTo: "resumes" }}
                                    className={`mt-3 w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed rounded-lg text-sm font-medium transition-colors ${
                                        isDarkMode
                                            ? "border-slate-600 text-slate-300 hover:border-primary-500 hover:text-primary-400"
                                            : "border-slate-300 text-slate-500 hover:border-primary-500 hover:text-primary-600"
                                    }`}
                                >
                                    <ExternalLink size={16} />
                                    Upload a resume in My Preferences
                                </Link>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={checking || orgCodeValidating || orgCodeValid !== true || !selectedResumeId}
                            className="px-6 py-2 bg-primary-600 text-white rounded-lg font-bold hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                        >
                            {checking ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Checking...
                                </>
                            ) : (
                                "Analyze Resume"
                            )}
                        </button>
                    </form>
                </div>
            </section>

            {checking && (
                <div className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-4 text-sm ${
                    isDarkMode ? "bg-primary-900/30 border border-primary-700 text-primary-300" : "bg-primary-50 border border-primary-200 text-primary-700"
                }`}>
                    <Loader2 size={16} className="animate-spin shrink-0" />
                    Checking your resume against {orgCodeName || "the organization"}'s standards — this usually takes a few seconds...
                </div>
            )}
        </>
    );
}
